import React, { useState, useEffect } from 'react';
import { Typography, Box, Button, Paper, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, TablePagination, CircularProgress, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { useTheme } from '../../contexts/ThemeContext';

const UploadHistory = () => {
  const navigate = useNavigate();
  const { themeMode } = useTheme();
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    total: 0,
    pages: 0,
    current_page: 1,
    per_page: 10
  });

  // Fetch upload history on component mount and when pagination changes
  useEffect(() => {
    fetchUploadHistory();
  }, [pagination.current_page, pagination.per_page]);

  const fetchUploadHistory = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current_page,
        per_page: pagination.per_page
      };
      
      const response = await api.get('/api/uploads/outstanding/history', { params });
      setUploads(response.data.uploads);
      setPagination({
        total: response.data.total,
        pages: response.data.pages,
        current_page: response.data.current_page,
        per_page: response.data.per_page
      });
    } catch (error) {
      console.error('Error fetching upload history:', error);
      toast.error('Failed to load upload history. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPagination({
      ...pagination,
      current_page: newPage + 1
    });
  };

  const handleChangeRowsPerPage = (event) => {
    setPagination({
      ...pagination,
      per_page: parseInt(event.target.value, 10),
      current_page: 1
    });
  };

  const handleNewUpload = () => {
    navigate('/uploads/outstanding');
  };


  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Upload History</Typography>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<FileUploadIcon />}
          onClick={handleNewUpload}
        >
          New Upload
        </Button>
      </Box>
      
      {loading ? (
        <Box display="flex" justifyContent="center" my={5}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>File Name</TableCell>
                  <TableCell>Uploaded By</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Total Records</TableCell>
                  <TableCell align="right">Success</TableCell>
                  <TableCell align="right">Failed</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {uploads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">No upload history found</TableCell>
                  </TableRow>
                ) : (
                  uploads.map((upload) => (
                    <TableRow key={upload.id}>
                      <TableCell>{upload.filename}</TableCell>
                      <TableCell>{upload.user.full_name || upload.user.username}</TableCell>
                      <TableCell>{formatDate(upload.created_at)}</TableCell>
                      <TableCell>
                        <Chip 
                          label={upload.status.charAt(0).toUpperCase() + upload.status.slice(1)} 
                          size="small"
                          variant="filled"
                          sx={{
                            ...(upload.status === 'completed' && {
                              bgcolor: 'success.main',
                              color: 'success.contrastText',
                              '& .MuiChip-label': {
                                color: 'success.contrastText',
                              }
                            }),
                            ...(upload.status === 'processing' && {
                              bgcolor: 'info.main',
                              color: 'info.contrastText',
                              '& .MuiChip-label': {
                                color: 'info.contrastText',
                              }
                            }),
                            ...(upload.status === 'pending' && {
                              bgcolor: 'warning.main',
                              color: 'warning.contrastText',
                              '& .MuiChip-label': {
                                color: 'warning.contrastText',
                              }
                            }),
                            ...(upload.status === 'failed' && {
                              bgcolor: 'error.main',
                              color: 'error.contrastText',
                              '& .MuiChip-label': {
                                color: 'error.contrastText',
                              }
                            })
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">{upload.total_records || '-'}</TableCell>
                      <TableCell align="right">{upload.successful_records || '-'}</TableCell>
                      <TableCell align="right">{upload.failed_records || '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={pagination.total}
            rowsPerPage={pagination.per_page}
            page={pagination.current_page - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}
    </Box>
  );
};

export default UploadHistory;